/**
 * "Continuar como Fulano" na tela do nome.
 *
 * Decisão do stakeholder (18/10/2026). Quem já consultou neste aparelho
 * confirmava o nome 70% das vezes, contra 79% de quem chegava pela primeira
 * vez, e no app Android houve paciente tocando no campo por minutos sem
 * conseguir digitar. Com a identidade salva (cookie/IndexedDB), um toque
 * confirma nome, e-mail, CPF e telefone.
 *
 * Cada tela continua dona da própria confirmação: aqui só gravamos os valores
 * como se esta aba os tivesse digitado e deixamos a lista de etapas que podem
 * confirmar sozinhas ao montar. Assim o registro no backend, o bloqueio de
 * acesso, a recuperação de pagamento e o cupom pós-CPF rodam pelo mesmo
 * código de sempre. Os Termos nunca entram na lista: aceite é sempre do
 * paciente.
 */

import { clearIdentityKey, getIdentityKeyAsync, persistIdentityKey } from './identityBackup';
import { ensureSignupSession } from './ensureSignupSession';
import { markSignupFieldTyped } from './signupFieldPrefill';
import { isValidEmail, isValidCpf } from './patientContactValidation';
import { isValidBrazilDdd, writePatientPhoneDial, DEFAULT_PHONE_DIAL } from './patientPhoneStorage';
import { isValidSignupFullName, normalizeSignupFullName } from './patientNameGuard';

export const QUICK_RESUME_STORAGE_KEY = 'signupQuickResume';
export const QUICK_RESUME_TTL_MS = 5 * 60 * 1000;

const AUTO_PATIENT_ID_RE = /^patient-\d{6,}(?:-[a-z0-9]+)*$/i;

function brazilNationalPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  let guard = 0;
  while (d.length > 11 && d.startsWith(DEFAULT_PHONE_DIAL) && guard < 6) {
    d = d.slice(2);
    guard += 1;
  }
  if (d.length < 10 || d.length > 11 || !isValidBrazilDdd(d)) return '';
  return d;
}

/**
 * Identidade salva no aparelho, só com os campos válidos. Sem nome válido
 * não há atalho: devolve null.
 */
export async function readSavedSignupIdentity() {
  const read = async (key) => {
    try {
      return String((await getIdentityKeyAsync(key)) || '').trim();
    } catch {
      return '';
    }
  };
  const rawName = (await read('fullName')) || (await read('patientName'));
  if (!rawName || AUTO_PATIENT_ID_RE.test(rawName) || !isValidSignupFullName(rawName)) return null;

  const email = (await read('patientEmail')).toLowerCase();
  const cpf = (await read('patientCpf')).replace(/\D/g, '');
  const phone = brazilNationalPhone(await read('patientPhone'));

  return {
    fullName: normalizeSignupFullName(rawName),
    email: isValidEmail(email) ? email : '',
    cpf: isValidCpf(cpf) ? cpf : '',
    phone,
  };
}

/** `m•••@gmail.com` — só para o paciente reconhecer o próprio e-mail. */
export function maskEmailForDisplay(email) {
  const [user, domain] = String(email || '').split('@');
  if (!user || !domain) return '';
  return `${user.charAt(0)}•••@${domain}`;
}

function writeField(key, value) {
  try {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  } catch {}
  try { persistIdentityKey(key, value); } catch {}
  markSignupFieldTyped(key);
}

/**
 * Grava e-mail, CPF e telefone salvos como digitados nesta aba e libera a
 * confirmação automática das etapas correspondentes. Campo inválido fica de
 * fora, e a cadeia para nele.
 */
export function startSignupQuickResume(identity) {
  const steps = [];
  if (identity?.email) {
    writeField('patientEmail', identity.email);
    try {
      sessionStorage.removeItem('patientEmailDraft');
      localStorage.removeItem('patientEmailDraft');
    } catch {}
    steps.push('email');
  }
  if (identity?.cpf) {
    writeField('patientCpf', identity.cpf);
    steps.push('cpf');
  }
  if (identity?.phone) {
    writePatientPhoneDial(DEFAULT_PHONE_DIAL, { omitIfBrazil: true });
    writeField('patientPhone', identity.phone);
    steps.push('telefone');
  }
  try {
    sessionStorage.setItem(QUICK_RESUME_STORAGE_KEY, JSON.stringify({ steps, at: Date.now() }));
  } catch {}
  return steps;
}

/**
 * A etapa pode confirmar sozinha? Consome a permissão: voltar à tela depois
 * mostra o campo preenchido, sem avançar de novo.
 */
export function takeSignupQuickResumeStep(step, now = Date.now()) {
  let state = null;
  try {
    state = JSON.parse(sessionStorage.getItem(QUICK_RESUME_STORAGE_KEY) || 'null');
  } catch {
    state = null;
  }
  if (!state || !Array.isArray(state.steps)) return false;
  if (!(now - Number(state.at || 0) <= QUICK_RESUME_TTL_MS)) {
    cancelSignupQuickResume();
    return false;
  }
  if (!state.steps.includes(step)) return false;
  const steps = state.steps.filter((s) => s !== step);
  try {
    if (steps.length) {
      sessionStorage.setItem(QUICK_RESUME_STORAGE_KEY, JSON.stringify({ steps, at: state.at }));
    } else {
      sessionStorage.removeItem(QUICK_RESUME_STORAGE_KEY);
    }
  } catch {}
  return true;
}

export function cancelSignupQuickResume() {
  try { sessionStorage.removeItem(QUICK_RESUME_STORAGE_KEY); } catch {}
}

const SAVED_IDENTITY_KEYS = ['fullName', 'patientName', 'patientEmail', 'patientCpf', 'patientPhone'];

/**
 * "É outra pessoa": o patientId e a identidade salvos são de quem usou o
 * aparelho antes. O backend só grava o primeiro nome, e-mail, CPF e telefone
 * de cada patientId, e o cartão seguinte juntaria o nome novo ao contato
 * antigo. Começa sessão nova e apaga a identidade salva.
 */
export async function startSignupAsAnotherPerson() {
  cancelSignupQuickResume();
  SAVED_IDENTITY_KEYS.forEach((key) => {
    try { clearIdentityKey(key); } catch {}
  });
  return ensureSignupSession({ forceNew: true, resetFields: true }).catch(() => null);
}
